import React, { createContext, useContext, useEffect, useState } from 'react'
import { showErrorToast, showSuccessToast } from '../../services/CommonServices/alertServices'
import { getAssignWorkersData, unassignWorker, assignWorker } from '../../services/laborService'
import { ThrownServiceError } from '../../types/Core/ThrownServiceError'
import { Employee } from '../../types/Employee/Employee'
import { JobRoleEnum } from '../../types/Employee/JobRole'

interface LaborContextType {
	availableEmployees: Employee[] | undefined
	assignedEmployees: Employee[] | undefined
	unassignEmployee: (employee: Employee) => void
	assignEmployee: (employeeFrom: Employee, employeeTo?: Employee) => void
	loadData: () => Promise<void>
	searchedAvailable: Employee[] | undefined
	searchedAssignedIds: number[] | undefined
	searchedQc: Employee[] | undefined
	setSearchText: React.Dispatch<React.SetStateAction<string>>
	searchText: string
	qcWorkers: Employee[] | undefined
	lineTechWorkers: Employee[] | undefined
	searchedLt: Employee[] | undefined
	materialWorkers: Employee[] | undefined
	searchedMaterial: Employee[] | undefined
}

const LaborContext = createContext<LaborContextType | null>(null)

const useLaborContext = (): LaborContextType => {
	const laborContextState = useContext(LaborContext)
	if (laborContextState === null) {
		throw new Error(
			'LaborContext not found. Try wrapping a parent component with <LaborContext.Provider>.',
		)
	}
	return laborContextState
}

type LaborProviderProps = {
	children?: React.ReactNode
}

const LaborProvider = ({ children }: LaborProviderProps) => {
	const [availableEmployees, setAvailableEmployees] = useState<Employee[] | undefined>()
	const [assignedEmployees, setAssignedEmployees] = useState<Employee[] | undefined>()

	const unassignEmployee = async (employee: Employee, fromSupervisor?: Employee) => {
		await unassignWorker(employee, fromSupervisor).then(() => {
			showSuccessToast(`${employee.firstName} ${employee.lastName} was unassigned.`)
		})
		await loadData()
	}

	const loadData = async () => {
		const { availableWorkers, assignedWorkers } = await getAssignWorkersData()
		const { availableQcWorkers, availableLtWorkers, availableConstructionWorkers, availableMaterialWorkers } =
			filterMiddlePanelWorkers(availableWorkers, assignedWorkers)
		setAvailableEmployees(availableConstructionWorkers)
		setAssignedEmployees(assignedWorkers)
		setQcWorkers(availableQcWorkers)
		setLineTechWorkers(availableLtWorkers)
		setMaterialWorkers(availableMaterialWorkers)
	}

	const assignEmployee = async (employeeFrom: Employee, employeeTo?: Employee) => {
		await assignWorker(employeeFrom, employeeTo)
			.then(async () => {
				showSuccessToast(
					`${employeeFrom.firstName} ${employeeFrom.lastName} was assigned to ${
						employeeTo ? `${employeeTo.firstName} ${employeeTo.lastName}` : 'undefined'
					}`,
				)
				await loadData()
			})
			.catch((err) => {
				const { eventId, message } = err as ThrownServiceError
				showErrorToast({
					eventId: eventId ?? '',
					traceId: 'projectService',
					message: message ?? '',
				})
			})
	}

	//#region Search
	const [searchText, setSearchText] = useState('')
	const [searchedAvailable, setSearchedAvailable] = useState<Employee[]>()
	const [searchedQc, setSearchedQc] = useState<Employee[]>()
	const [searchedLt, setSearchedLt] = useState<Employee[]>()
	const [searchedMaterial, setSearchedMaterial] = useState<Employee[]>()
	const [searchedAssignedIds, setSearchedAssignedIds] = useState<number[]>()

	useEffect(() => {
		if (searchText.trim() === '') {
			setSearchedAvailable(undefined)
			setSearchedAssignedIds(undefined)
			setSearchedQc(undefined)
			setSearchedLt(undefined)
			setSearchedMaterial(undefined)
			return
		}

		const lowerCaseSearchTerm = searchText.toLowerCase()

		// Filter available employees
		const filteredAvailable = availableEmployees?.filter((employee) => {
			const fullName = `${employee.firstName.toLowerCase()} ${employee.lastName.toLowerCase()}`
			return fullName.includes(lowerCaseSearchTerm)
		})
		setSearchedAvailable(filteredAvailable)

		// Filter QC employees
		const filteredQc = qcWorkers?.filter((employee) => {
			const fullName = `${employee.firstName.toLowerCase()} ${employee.lastName.toLowerCase()}`
			return fullName.includes(lowerCaseSearchTerm)
		})
		setSearchedQc(filteredQc)

		// Filter Line Tech employees
		const filteredLt = lineTechWorkers?.filter((employee) => {
			const fullName = `${employee.firstName.toLowerCase()} ${employee.lastName.toLowerCase()}`
			return fullName.includes(lowerCaseSearchTerm)
		})
		setSearchedLt(filteredLt)

		// Filter Material employees
		const filteredMaterial = materialWorkers?.filter((employee) => {
			const fullName = `${employee.firstName.toLowerCase()} ${employee.lastName.toLowerCase()}`
			return fullName.includes(lowerCaseSearchTerm)
		})
		setSearchedMaterial(filteredMaterial)

		const highlightedAssignedWorkers: number[] = []
		const recursiveSearch = (employees: Employee[], supervisors: number[] = []) => {
			employees.forEach((employee) => {
				const currentPath = [...supervisors, employee.id]
				const fullName = `${employee.firstName.toLowerCase()} ${employee.lastName.toLowerCase()}`
				// Check if the employee matches the search term
				if (fullName.includes(lowerCaseSearchTerm)) {
					highlightedAssignedWorkers.push(...currentPath) // Add supervisors and the matched employee
					return // Stop searching deeper (exclude direct reports)
				}

				// Recurse into direct reports if no match is found
				if (employee.directReports) {
					recursiveSearch(employee.directReports, currentPath)
				}
			})
		}
		recursiveSearch(assignedEmployees ?? [])
		setSearchedAssignedIds(Array.from(new Set(highlightedAssignedWorkers)))
	}, [searchText])

	//#endregion

	//#region Middle Panel Workers
	const [qcWorkers, setQcWorkers] = useState<Employee[] | undefined>()
	const [lineTechWorkers, setLineTechWorkers] = useState<Employee[] | undefined>()
	const [materialWorkers, setMaterialWorkers] = useState<Employee[] | undefined>()

	const filterMiddlePanelWorkers = (availableWorkers: Employee[], assignedWorkers: Employee[]) => {
		const { qcWorkers, ltWorkers, constructionWorkers, materialWorkers } = availableWorkers.reduce(
			(acc, worker) => {
				if (
					worker.role.title === 'QualityControl' ||
					worker.role.title === 'QualityControlManager'
				) {
					acc.qcWorkers.push(worker)
				} else if (worker.role.title === 'LineTech' || worker.role.title === 'LineTechManager') {
					acc.ltWorkers.push(worker)
				} else if (worker.role.title === 'MaterialManager' || worker.role.title === 'Material') {
					acc.materialWorkers.push(worker)
				} else {
					acc.constructionWorkers.push(worker)
				}
				return acc
			},
			{
				qcWorkers: [] as Employee[],
				ltWorkers: [] as Employee[],
				constructionWorkers: [] as Employee[],
				materialWorkers: [] as Employee[],
			},
		)

		const assignedQcWorkers = Array.from(
			new Set(filterEmployeesByRole(assignedWorkers, 'QualityControl')),
		)
		const assignedLtWorkers = Array.from(
			new Set(filterEmployeesByRole(assignedWorkers, 'LineTech')),
		)
		return {
			availableQcWorkers: [...qcWorkers, ...assignedQcWorkers],
			availableLtWorkers: [...ltWorkers, ...assignedLtWorkers],
			availableConstructionWorkers: constructionWorkers,
			availableMaterialWorkers: materialWorkers,
		}
	}
	const filterEmployeesByRole = (employees: Employee[], role: JobRoleEnum): Employee[] => {
		const result = new Map<number, Employee>()

		const traverse = (employeeList: Employee[]) => {
			for (const employee of employeeList) {
				if (employee.role.title === role && !result.has(employee.id)) {
					result.set(employee.id, employee)
				}
				if (employee.directReports && employee.directReports.length > 0) {
					traverse(employee.directReports) // Recursively check direct reports
				}
			}
		}

		traverse(employees)
		return Array.from(result.values())
	}
	//#endregion

	return (
		<LaborContext.Provider
			value={{
				searchedQc,
				qcWorkers,
				lineTechWorkers,
				availableEmployees,
				assignedEmployees,
				assignEmployee,
				unassignEmployee,
				loadData,
				searchText,
				setSearchText,
				searchedAssignedIds,
				searchedAvailable,
				searchedLt,
				materialWorkers,
				searchedMaterial,
			}}
		>
			{children}
		</LaborContext.Provider>
	)
}

export { useLaborContext, LaborProvider }
